export const API_ROOT_PATH = "/api" as const;

export enum ProductApiId {
  Compass = "compass",
  Couple = "couple",
  Fortuna = "fortuna",
  Helmwise = "helmwise",
  Jarvis = "jarvis",
  Lightyear = "lightyear",
  Puzzle = "puzzle",
  Steward = "steward",
}

export enum PlatformApiScope {
  AppConnections = "/app-connections",
  AppGovernance = "/app-governance",
  AppRegistry = "/app-registry",
  Auth = "/auth",
  Entitlement = "/entitlement",
  ExchangeRates = "/exchange-rates",
  Governance = "/admin",
  Sync = "/sync",
  Workday = "/workday",
}

export type ApiRelativePath = `/${string}`;
export type ApiRoutePath = `${typeof API_ROOT_PATH}${ApiRelativePath}`;

export function toApiRoute(path: ApiRelativePath): ApiRoutePath {
  return `${API_ROOT_PATH}${path}`;
}

export function toProductApiRoute(
  productId: ProductApiId,
  path: ApiRelativePath,
): ApiRoutePath {
  return `${API_ROOT_PATH}/${productId}${path}`;
}

export function toProductApiRoot(
  productId: ProductApiId,
): `${typeof API_ROOT_PATH}/${ProductApiId}` {
  return `${API_ROOT_PATH}/${productId}`;
}

export function encodeApiPathSegment(value: string | number): string {
  return encodeURIComponent(String(value));
}

export type ApiQueryValue = string | number | boolean | null | undefined;

export function withApiQuery(
  path: ApiRelativePath,
  query: Readonly<Record<string, ApiQueryValue>>,
): ApiRelativePath {
  const search = Object.entries(query)
    .flatMap(([key, value]) =>
      value === undefined || value === null
        ? []
        : `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    )
    .join("&");
  return search ? `${path}?${search}` : path;
}

export enum PlatformApiPath {
  AppConnections = "/app-connections",
  AppConnectionRevoke = "/app-connections/:id/revoke",
  AppRegistryEffective = "/app-registry/effective",
  AuthRoot = "/auth",
  AuthConfig = "/auth/config",
  AuthAccountBindingDisconnect = "/auth/account-bindings/:provider/disconnect",
  AuthAccountBindingAuthorize = "/auth/account-bindings/:provider/authorize",
  AuthAccountBindingCallback = "/auth/account-bindings/:provider/callback",
  AuthAccountBindings = "/auth/account-bindings",
  AuthLogout = "/auth/logout",
  AuthMe = "/auth/me",
  AuthMeUpdate = "/auth/me/update",
  AuthProviders = "/auth/providers",
  EntitlementDiscountCodeRedeem = "/entitlement/discount-codes/redeem",
  EntitlementTier = "/entitlement/tier",
  EntitlementToken = "/entitlement/token",
  ExchangeRatesLatest = "/exchange-rates/latest",
  Health = "/health",
  Metrics = "/metrics",
  Ready = "/ready",
  SyncCollaborationAudit = "/sync/collaboration/workspaces/:id/audit",
  SyncCollaborationInvitations = "/sync/collaboration/invitations",
  SyncCollaborationInvitationRespond = "/sync/collaboration/invitations/:id/respond",
  SyncCollaborationMembers = "/sync/collaboration/workspaces/:id/members",
  SyncCollaborationMemberRemove = "/sync/collaboration/workspaces/:id/members/:userId/remove",
  SyncCollaborationMemberUpdate = "/sync/collaboration/workspaces/:id/members/:userId/update",
  SyncCollaborationResources = "/sync/collaboration/workspaces/:id/resources",
  SyncCollaborationResourceDelete = "/sync/collaboration/workspaces/:id/resources/:resourceId/delete",
  SyncCollaborationResourceUpsert = "/sync/collaboration/workspaces/:id/resources/upsert",
  SyncCollaborationWorkspaceInvitations = "/sync/collaboration/workspaces/:id/invitations",
  SyncCollaborationWorkspaces = "/sync/collaboration/workspaces",
  SyncEntitiesPull = "/sync/entities/pull",
  SyncEntitiesPush = "/sync/entities/push",
  SyncEntitiesRetry = "/sync/entities/retry",
  SyncLegacyDocument = "/sync/:key",
  SyncLegacyDocumentUpdate = "/sync/:key/update",
  WorkdayHolidays = "/workday/holidays",
}

export const platformApiPath = {
  appConnectionRevoke: (connectionId: string): ApiRelativePath =>
    `/app-connections/${encodeApiPathSegment(connectionId)}/revoke`,
  externalAccountBindingDisconnect: (provider: string): ApiRelativePath =>
    `/auth/account-bindings/${encodeApiPathSegment(provider)}/disconnect`,
  externalAccountBindingAuthorize: (provider: string): ApiRelativePath =>
    `/auth/account-bindings/${encodeApiPathSegment(provider)}/authorize`,
  externalAccountBindingCallback: (provider: string): ApiRelativePath =>
    `/auth/account-bindings/${encodeApiPathSegment(provider)}/callback`,
  collaborationAudit: (workspaceId: string): ApiRelativePath =>
    `/sync/collaboration/workspaces/${encodeApiPathSegment(workspaceId)}/audit`,
  collaborationInvitationRespond: (invitationId: string): ApiRelativePath =>
    `/sync/collaboration/invitations/${encodeApiPathSegment(invitationId)}/respond`,
  collaborationMemberRemove: (
    workspaceId: string,
    userId: string,
  ): ApiRelativePath =>
    `/sync/collaboration/workspaces/${encodeApiPathSegment(workspaceId)}/members/${encodeApiPathSegment(userId)}/remove`,
  collaborationMemberUpdate: (
    workspaceId: string,
    userId: string,
  ): ApiRelativePath =>
    `/sync/collaboration/workspaces/${encodeApiPathSegment(workspaceId)}/members/${encodeApiPathSegment(userId)}/update`,
  collaborationMembers: (workspaceId: string): ApiRelativePath =>
    `/sync/collaboration/workspaces/${encodeApiPathSegment(workspaceId)}/members`,
  collaborationResourceDelete: (
    workspaceId: string,
    resourceId: string,
  ): ApiRelativePath =>
    `/sync/collaboration/workspaces/${encodeApiPathSegment(workspaceId)}/resources/${encodeApiPathSegment(resourceId)}/delete`,
  collaborationResources: (workspaceId: string): ApiRelativePath =>
    `/sync/collaboration/workspaces/${encodeApiPathSegment(workspaceId)}/resources`,
  collaborationResourceUpsert: (workspaceId: string): ApiRelativePath =>
    `/sync/collaboration/workspaces/${encodeApiPathSegment(workspaceId)}/resources/upsert`,
  collaborationWorkspaceInvitations: (workspaceId: string): ApiRelativePath =>
    `/sync/collaboration/workspaces/${encodeApiPathSegment(workspaceId)}/invitations`,
  legacySyncDocument: (key: string): ApiRelativePath =>
    `/sync/${encodeApiPathSegment(key)}`,
  legacySyncDocumentUpdate: (key: string): ApiRelativePath =>
    `/sync/${encodeApiPathSegment(key)}/update`,
} as const;
